import { configureStore, combineReducers } from '@reduxjs/toolkit';
import uiSlice from './slices/uiSlice.ts';
import authSlice from './slices/authSlice.ts';
import menuSlice from './slices/menuSlice.ts';
import permissionSlice from './slices/permissionSlice.ts';
import userPreferencesSlice from './slices/userPreferencesSlice.ts';
import {
  loggerMiddleware,
  notificationMiddleware,
  persistenceMiddleware,
} from './middleware/index.ts';
import { getStoredAuth } from '@/utils/storage';
import { authService, apiClient } from '@/api';
import type { AuthState } from '@/api';


type PreloadedAuthState = Partial<{ auth: AuthState }>;

let preloadedState: PreloadedAuthState | undefined;
if (typeof window !== 'undefined') {
  try {
    const stored = getStoredAuth();
    if (stored) {
      const expiry = new Date(stored.tokenExpiry);
      if (!authService.isTokenExpired(expiry)) {
        preloadedState = {
          auth: {
            user: stored.user,
            token: stored.token,
            tokenExpiry: stored.tokenExpiry, // already ISO string
            isAuthenticated: true,
            loading: false,
            error: null,
            isSSO: stored.isSSO ?? false,
            isInitializing: false,
            accountLinkRequired: false,
            rexSystems: stored.rexSystems ?? [],
          },
        };
      }
    }
  } catch {
    // eslint-disable-next-line no-console -- hydration failure is non-fatal
    console.log('[store] auth preload hydration failed ');
  }
}

const rootReducer = combineReducers({
  ui: uiSlice,
  auth: authSlice,
  menu: menuSlice,
  permissions: permissionSlice,
  userPreferences: userPreferencesSlice,
});

export const store = configureStore({
  reducer: rootReducer,
  preloadedState: preloadedState as any,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['ui/openModal'],
        ignoredPaths: ['ui.modals'],
      },
    }).concat(
      loggerMiddleware as any,
      notificationMiddleware as any,
      persistenceMiddleware as any,
    ),
  devTools: true,
});

apiClient.setTokenProvider(() => {
  try {
    return store.getState().auth.token || null;
  } catch {
    return null;
  }
});


export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

